/**
 * ResearcherDashboardSection — the StudySync researcher console, rendered as ONE
 * unified bordered "dashboard window" (not three floating cards). The whole
 * widget is a single outer panel laid out as a GRID and split into panes by
 * internal divider rules, like a real app UI split into resizable panes.
 *
 * Eyebrow "THE RESEARCHER DASHBOARD" + heading "SEE EVERY DEVICE, LIVE", then the
 * dashboard window:
 *
 *   LEFT PANE (~58%, lg:col-span-3) — the GCS JSON VIEWER. A window-chrome bar
 *     (three traffic-light dots + HEALTHKIT / FITBIT / EHR source tabs + a copy
 *     button), a gs:// path caption, then the export JSON for the active source
 *     rendered with a LINE-NUMBER GUTTER and light syntax coloring in a
 *     terminal-dark teal pane. The scroll container hides its scrollbar but stays
 *     scrollable; the JSON is hand-compacted so NO horizontal scroll is needed at
 *     the pane width.
 *
 *   RIGHT PANE (~42%, lg:col-span-2) — split top/bottom by an internal rule:
 *     TOP a 2×2 grid of four KPIs (DATA SENT / LAST SYNCED / LAST ONBOARDED /
 *     TOTAL PARTICIPANTS), BOTTOM the adherence HEATMAP (24 weeks × 7 days,
 *     deterministic teal intensity, Less→More legend).
 *
 * DATA AUTHENTICITY: the JSON samples mirror the real backend export shapes —
 *   - HealthKit / Fitbit wearable wrapper: `{ summary, records:[...] }` where each
 *     record nests activity / heartRate / sleep / hrv / spo2 (see
 *     newbackend/.../fitbitExtractionExportService.js `formatRecord`). Compacted
 *     for the pane width but structurally faithful.
 *   - EHR: a JSON array of FHIR resources with recordType / fhirResource /
 *     extractedText (see newbackend/.../ehrExportService.js `queryEHRRecords`).
 *   - GCS path: gs://study-{id}-data/{healthkit-extractions|extractions|
 *     ehr-extractions}/{participant}/{researcher}/{name}.json.
 *
 * COLORS: section background is brand Teal `bg-secondary` (#00505C), matching the
 * Trusted-by band + footer; the window surface + JSON pane use DARKER shades of
 * the same teal family (#0a2e34 / #06262b) so the JSON reads like a terminal —
 * never pure black. Light/cream text + borders for contrast.
 *
 * Tailwind Preflight is OFF → every border is declared `border-solid`. Brutalist:
 * zero border-radius EXCEPT the three traffic-light dots. framer-motion provides a
 * reduced-motion-safe reveal (static state shows everything). ZERO MUI.
 */
import * as React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Check, Copy } from 'lucide-react';
import StatCell from './shared/StatCell';
import WindowChrome, { WindowTab } from './shared/WindowChrome';

/* ────────────────────────────────────────────────────────────────────────────
 * GCS JSON viewer — data
 * ──────────────────────────────────────────────────────────────────────────── */

type SourceKey = 'healthkit' | 'fitbit' | 'ehr';

interface SourceTab extends WindowTab {
  key: SourceKey;
  path: string;
  json: string;
}

/* The samples below mirror the AUTHENTIC GCS export shapes (see file header) but
 * are hand-compacted — short keys/values, arrays broken across lines — so every
 * line fits the LEFT pane WITHOUT horizontal scroll. */

const HEALTHKIT_JSON = `{
  "summary": {
    "totalRecords": 184,
    "dateRange": {
      "start": "2025-11-01",
      "end": "2026-05-13"
    },
    "dataTypes": "ALL"
  },
  "records": [
    {
      "date": "2025-11-11",
      "activity": {
        "steps": 5661,
        "calories": 2143,
        "distanceKm": 4.21,
        "activeMin": 69
      },
      "heartRate": {
        "resting": 61,
        "intraday": [
          { "t": "08:00", "bpm": 72 },
          { "t": "08:01", "bpm": 76 },
          { "t": "08:02", "bpm": 81 }
        ]
      },
      "sleep": {
        "durationMin": 226,
        "efficiency": 91,
        "stages": {
          "deep": 45,
          "core": 152,
          "rem": 29,
          "awake": 4
        }
      },
      "hrv": { "rmssd": 89.02 },
      "spo2": { "avg": 96.4, "min": 93.1 }
    }
  ]
}`;

const FITBIT_JSON = `{
  "summary": {
    "totalRecords": 211,
    "dateRange": {
      "start": "2025-10-20",
      "end": "2026-05-13"
    },
    "dataTypes": "ALL"
  },
  "records": [
    {
      "date": "2025-12-16",
      "activity": {
        "steps": 8472,
        "calories": 2390,
        "distanceKm": 6.13,
        "activeMin": 81
      },
      "heartRate": {
        "resting": 63,
        "zones": [
          { "name": "Fat Burn", "min": 34 },
          { "name": "Cardio", "min": 11 },
          { "name": "Peak", "min": 14 }
        ]
      },
      "sleep": {
        "durationMin": 411,
        "efficiency": 94
      },
      "hrv": { "rmssd": 43.27 },
      "spo2": { "avg": 96.3, "min": 93.9 }
    }
  ]
}`;

const EHR_JSON = `[
  {
    "recordType": "lab_result",
    "displayName": "LDL-cholesterol",
    "clinicalDate": "2023-10-18",
    "source": "Stanford Health Care",
    "fhirResource": {
      "id": "1",
      "resourceType": "Observation",
      "status": "final",
      "code": {
        "text": "LDL-cholesterol",
        "loinc": "2089-1"
      },
      "valueQuantity": {
        "value": 113.3,
        "unit": "mg/dL"
      },
      "refRange": {
        "low": 50,
        "high": 178
      }
    },
    "extractedText": "LDL 113.3 mg/dL"
  },
  {
    "recordType": "allergy",
    "displayName": "Penicillin",
    "clinicalDate": "2010-04-12",
    "fhirResource": {
      "id": "2",
      "resourceType": "AllergyIntolerance",
      "status": "confirmed",
      "substance": {
        "text": "Penicillin",
        "snomed": "373270004"
      },
      "reaction": {
        "text": "Rash",
        "severity": "mild"
      }
    },
    "extractedText": "Penicillin — mild rash"
  }
]`;

const SOURCE_TABS: SourceTab[] = [
  {
    key: 'healthkit',
    label: 'HealthKit',
    path: 'gs://study-7f3a-data/healthkit-extractions/P-0241/r.chen/hk_2026-05-13.json',
    json: HEALTHKIT_JSON,
  },
  {
    key: 'fitbit',
    label: 'Fitbit',
    path: 'gs://study-7f3a-data/extractions/P-0250/r.chen/fitbit_2026-05-13.json',
    json: FITBIT_JSON,
  },
  {
    key: 'ehr',
    label: 'EHR',
    path: 'gs://study-7f3a-data/ehr-extractions/P-0241/r.chen/ehr_2026-05-13.json',
    json: EHR_JSON,
  },
];

/* ────────────────────────────────────────────────────────────────────────────
 * GCS JSON viewer — syntax highlighting
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Lightweight JSON syntax highlighter — tokenizes keys, strings, numbers,
 * booleans/null, and punctuation into colored <span>s. Purely cosmetic; the
 * regex is anchored per token so it never mangles the structure. Operates on a
 * single line at a time so each line can sit next to its gutter number.
 */
const TOKEN_RE =
  /("(?:\\.|[^"\\])*"\s*:)|("(?:\\.|[^"\\])*")|(\b-?\d+\.?\d*\b)|(\btrue\b|\bfalse\b|\bnull\b)|([{}[\],])/g;

function highlightLine(src: string, lineKey: number): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  for (const m of Array.from(src.matchAll(TOKEN_RE))) {
    const idx = m.index ?? 0;
    if (idx > lastIndex) out.push(src.slice(lastIndex, idx));
    const [full, propKey, str, num, lit, punct] = m;
    const k = `${lineKey}-${key++}`;
    if (propKey) {
      out.push(
        <span key={k} className="text-[#5BD0DE]">
          {propKey}
        </span>,
      );
    } else if (str) {
      out.push(
        <span key={k} className="text-[#9BD17A]">
          {str}
        </span>,
      );
    } else if (num) {
      out.push(
        <span key={k} className="text-[#F5C518]">
          {num}
        </span>,
      );
    } else if (lit) {
      out.push(
        <span key={k} className="text-[#E0504F]">
          {lit}
        </span>,
      );
    } else if (punct) {
      out.push(
        <span key={k} className="text-white/35">
          {punct}
        </span>,
      );
    } else {
      out.push(full);
    }
    lastIndex = idx + full.length;
  }
  if (lastIndex < src.length) out.push(src.slice(lastIndex));
  return out;
}

/**
 * A code block with a fixed left LINE-NUMBER GUTTER aligned to each line, light
 * syntax coloring, and a HIDDEN-but-scrollable container (scrollbar suppressed
 * via arbitrary `scrollbar-width:none` + webkit pseudo). Fixed max-height so it
 * scrolls vertically; the compacted JSON guarantees no horizontal scroll.
 */
const CodeBlock: React.FC<{ source: string }> = ({ source }) => {
  const lines = React.useMemo(() => source.split('\n'), [source]);
  const gutterWidth = String(lines.length).length;

  return (
    <div className="max-h-[420px] overflow-auto overscroll-auto bg-[#06262b] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <pre className="m-0 flex font-mono text-[11px] leading-relaxed sm:text-xs">
        {/* Gutter: right-aligned line numbers, non-selectable. */}
        <code
          aria-hidden
          className="select-none border-r border-solid border-white/10 bg-[#04201f] px-3 py-4 text-right text-white/25"
        >
          {lines.map((_, i) => (
            <span key={i} className="block tabular-nums">
              {String(i + 1).padStart(gutterWidth, ' ')}
            </span>
          ))}
        </code>
        {/* Body: one highlighted line per row, aligned to the gutter. */}
        <code className="block flex-1 whitespace-pre px-4 py-4 text-white/85">
          {lines.map((ln, i) => (
            <span key={i} className="block">
              {highlightLine(ln, i)}
              {/* keep empty lines from collapsing */}
              {ln.length === 0 ? '​' : null}
            </span>
          ))}
        </code>
      </pre>
    </div>
  );
};

/** The LEFT pane: window chrome (dots + tabs + copy) over the gs:// path + code. */
const JsonViewerPane: React.FC = () => {
  const [active, setActive] = React.useState<SourceKey>('healthkit');
  const [copied, setCopied] = React.useState(false);
  const tab = SOURCE_TABS.find((t) => t.key === active) ?? SOURCE_TABS[0];

  const handleCopy = React.useCallback(() => {
    // Best-effort; clipboard may be unavailable in some embed contexts.
    void navigator.clipboard?.writeText(tab.json).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1400);
      },
      () => undefined,
    );
  }, [tab.json]);

  const copyButton = (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? 'Copied' : 'Copy JSON'}
      title={copied ? 'Copied' : 'Copy JSON'}
      className="flex items-center gap-1.5 border border-solid border-white/20 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-white/55 transition-colors hover:border-white/40 hover:text-white/85 sm:text-xs"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-[#5BD0DE]" strokeWidth={2.5} aria-hidden />
      ) : (
        <Copy className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
      )}
      <span className="hidden sm:inline">{copied ? 'Copied' : 'Copy'}</span>
    </button>
  );

  return (
    <div className="flex h-full flex-col">
      <WindowChrome
        tabs={SOURCE_TABS}
        activeKey={active}
        onSelect={(k) => setActive(k as SourceKey)}
        tablistLabel="Export source"
        trailing={copyButton}
      />

      {/* gs:// path caption above the body. */}
      <div className="border-b-2 border-solid border-white/20 bg-[#0a2e34] px-4 py-2">
        <span className="block truncate font-mono text-[11px] text-white/45" title={tab.path}>
          {tab.path}
        </span>
      </div>

      <CodeBlock source={tab.json} />
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────────────
 * Right pane — KPIs
 * ──────────────────────────────────────────────────────────────────────────── */

interface DashKpi {
  value: string;
  label: string;
  color: string;
  delta?: { text: string; className?: string };
}

const DASH_KPIS: DashKpi[] = [
  { value: '49.6K', label: 'Daily Records', color: 'text-[#4ED88B]' },
  { value: '2 min ago', label: 'Last Synced', color: 'text-[#5BD0DE]' },
  { value: 'P-0248', label: 'Last Onboarded', color: 'text-[#F5C518]', delta: { text: '14 min ago', className: 'text-white/40' } },
  { value: '248', label: 'Total Participants', color: 'text-[#E0504F]' },
];

/** TOP sub-pane: the four fake KPIs in a tidy 2×2 grid, divided by inner rules. */
const KpiGrid: React.FC = () => (
  <div className="grid grid-cols-2">
    {DASH_KPIS.map((kpi, i) => (
      <StatCell
        key={kpi.label}
        label={kpi.label}
        size="md"
        align="start"
        valueClassName={kpi.color}
        labelClassName="text-white/45"
        delta={kpi.delta}
        className={[
          'px-4 py-5 sm:px-5 sm:py-6',
          // Left divider on the right column.
          i % 2 === 1 ? 'border-l-2 border-solid border-white/15' : '',
          // Top divider on the bottom row.
          i >= 2 ? 'border-t-2 border-solid border-white/15' : '',
        ].join(' ')}
      >
        {kpi.value}
      </StatCell>
    ))}
  </div>
);

/* ────────────────────────────────────────────────────────────────────────────
 * Right pane — adherence heatmap
 * ──────────────────────────────────────────────────────────────────────────── */

const HEATMAP_WEEKS = 24;
const HEATMAP_DAYS = 7; // rows: Sun..Sat
const HEATMAP_ENROLLED = 248;

/**
 * Deterministic per-cell participant-record count for the adherence heatmap.
 * Seeded purely by (week, day) — NO Math.random — so the grid is stable across
 * renders/SSR. The model:
 *   - a study RAMP-UP: enrollment climbs from ~18% to ~100% over the 24 weeks;
 *   - WEEKDAY-heavier adherence (weekends dip a little);
 *   - a cheap hash adds day-to-day jitter;
 *   - two deliberate LOW-ADHERENCE dips (a holiday lull at week 9, an outage at
 *     week 17) carve visible cool patches.
 * Returns a participant count in [0, HEATMAP_ENROLLED].
 */
function adherenceCount(week: number, day: number): number {
  // Ramp: fraction of the cohort enrolled & active by this week.
  const ramp = 0.18 + 0.82 * (week / (HEATMAP_WEEKS - 1));

  // Weekday weighting (Sun=0 … Sat=6): weekends adhere a bit less.
  const weekend = day === 0 || day === 6;
  const dayWeight = weekend ? 0.78 : 0.97;

  // Deterministic jitter from a small integer hash → [0.85, 1.0].
  const h = (week * 73856093) ^ (day * 19349663);
  const jitter = 0.85 + (((h >>> 0) % 1000) / 1000) * 0.15;

  // Deliberate dips: a holiday lull and a sync outage.
  let dip = 1;
  if (week === 9) dip = 0.45; // holiday lull
  if (week === 17 && day >= 2 && day <= 4) dip = 0.3; // mid-week outage

  const frac = Math.min(1, Math.max(0, ramp * dayWeight * jitter * dip));
  return Math.round(frac * HEATMAP_ENROLLED);
}

/** 5-step teal intensity scale on the dark teal panel (index 0 = empty/coolest). */
const HEAT_SCALE = ['#0c343a', '#0E4A4A', '#11707A', '#1FA0AE', '#3FD0DC'];

function heatLevel(count: number): number {
  const frac = count / HEATMAP_ENROLLED;
  if (frac <= 0.001) return 0;
  if (frac < 0.35) return 1;
  if (frac < 0.6) return 2;
  if (frac < 0.85) return 3;
  return 4;
}

/** BOTTOM sub-pane: GitHub-style 24×7 calendar heatmap with a Less→More legend. */
const AdherenceHeatmap: React.FC = () => {
  // Build column-major (week) arrays of 7 day-cells.
  const weeks = React.useMemo(
    () =>
      Array.from({ length: HEATMAP_WEEKS }, (_, w) =>
        Array.from({ length: HEATMAP_DAYS }, (_, d) => {
          const count = adherenceCount(w, d);
          return { level: heatLevel(count), count, w, d };
        }),
      ),
    [],
  );

  return (
    <div className="p-5 sm:p-6">
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h3 className="font-head text-base uppercase tracking-tight text-white/90 sm:text-lg">
          Adherence heatmap
        </h3>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40 sm:text-xs">
          Last 24 weeks
        </span>
      </div>

      {/* Horizontal-scroll guard on very narrow screens; scrollbar hidden. */}
      <div className="overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex gap-[3px]">
          {weeks.map((col, w) => (
            <div key={w} className="flex flex-col gap-[3px]">
              {col.map((cell) => (
                <div
                  key={cell.d}
                  title={`Week ${cell.w + 1}, day ${cell.d + 1}: ${cell.count} participants sent records`}
                  className="h-3 w-3 border border-solid border-white/10 sm:h-3.5 sm:w-3.5"
                  style={{ backgroundColor: HEAT_SCALE[cell.level] }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* less → more legend. */}
      <div className="mt-4 flex items-center justify-end gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/40">Less</span>
        <div className="flex gap-[3px]">
          {HEAT_SCALE.map((c) => (
            <span
              key={c}
              className="h-3 w-3 border border-solid border-white/10"
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/40">More</span>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────────────
 * Section shell
 * ──────────────────────────────────────────────────────────────────────────── */

const ResearcherDashboardSection: React.FC = () => {
  const reduceMotion = useReducedMotion();

  const reveal = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: { duration: 0.5, ease: 'easeOut' as const },
      };

  return (
    <section
      id="dashboard"
      className="bg-secondary px-4 py-20 md:px-8 md:py-28"
    >
      {/* Header — yellow eyebrow + big cream uppercase heading. */}
      <div className="mx-auto max-w-6xl text-center">
        <p className="mb-4 inline-flex items-center border-2 border-solid border-black bg-[#F5C518] px-3 py-1 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#1A1A1A]">
          The researcher dashboard
        </p>
        <h2 className="font-head text-3xl uppercase leading-tight tracking-tight text-[#FAFAFA] sm:text-4xl lg:text-5xl">
          One Dashboard, See <span className="text-yellow">everything</span>
        </h2>
        <p className="mx-auto mt-4 max-w-2xl font-sans text-base text-white/65">
          One console for the whole cohort — the exact JSON that lands in your
          storage bucket, the cohort vitals, and adherence at a glance.
        </p>
      </div>

      {/* ONE unified dashboard window: a single bordered panel split into panes by
          internal divider rules. 2-col (5-track) grid at lg, stacked on mobile. */}
      <motion.div {...reveal} className="mx-auto mt-14 max-w-6xl">
        <div className="grid border-2 border-solid border-white/80 bg-[#0a2e34] lg:grid-cols-5">
          {/* LEFT PANE — JSON viewer (~58%). Bottom rule on mobile becomes a right
              rule at lg, so the two panes read as one split window. */}
          <div className="min-w-0 border-b-2 border-solid border-white/80 lg:col-span-3 lg:border-b-0 lg:border-r-2">
            <JsonViewerPane />
          </div>

          {/* RIGHT PANE (~42%) — KPIs over heatmap, split by an internal rule. */}
          <div className="flex min-w-0 flex-col lg:col-span-2">
            <KpiGrid />
            <div className="border-t-2 border-solid border-white/15">
              <AdherenceHeatmap />
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default ResearcherDashboardSection;
